import React, { useEffect, useState } from "react";
import FormTemplate from "../../../forms/FormTemplate";
import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  lighten,
} from "@mui/material";
import "../../../styles/SMSSideMenu.css";
import useAutomations from "../../../hooks/automationhooks/useAutomations";
import WhatsappTemplateSelection from "./WhatsappTemplateSelection";
import SelectArrow from "../../../styles/icons/SelectArrow";
import ROUTES from "../../../routerfolder/Routes";
import { useParams } from "react-router-dom";
import TriggerModeHeaderControl from "./TriggerModeHeaderControl";

export default function WhatsappSideMenu({
  onClose,
  onSubmit,
  selectedNode,
  preSelectedTemplate = null,
}) {
  const COLOR_SURFACE = "#2C2B2D";
  const COLOR_HEADER = "#1E1E1E";
  const COLOR_ACCENT = "#0DA3E2";
  const CHANGE_TEMPLATE_ICON_BG =
    "url(\"data:image/svg+xml,%3Csvg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M17 7H1L6.5 1M1 11H17L11.5 17' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")";
  //FOR AUTH CHECKS LATER
  const isAuthorized = false;
  const {
    isLoading,
    whatsappTemplates,
    handleGetWhatsappTemplates,
    setLoading,
    handleGetSingleWhatsappTemplate,
  } = useAutomations();
  const { brandId } = useParams();
  const [selected, setSelected] = useState(preSelectedTemplate);
  const [templateSelectionOpen, setTemplateSelectionOpen] = useState(false);
  const [valid, setValid] = useState(false);

  // Track placeholder values
  const [placeholderValues, setPlaceholderValues] = useState({});

  // State to hold the correct template message
  const [messageText, setMessageText] = useState("");
  const [templateParams, setTemplateParams] = useState([]); // Store template parameters
  const [templateButtons, setTemplateButtons] = useState([]);
  const [triggerMode, setTriggerMode] = useState("regular");

  // Fetch templates when component mounts
  useEffect(() => {
    // ask the cache first; only fetch if we haven't yet
    if (!whatsappTemplates?.data?.Data?.List?.length) {
      handleGetWhatsappTemplates(brandId);
    }
  }, [brandId]); // ← runs once per brand

  useEffect(() => {
    if (whatsappTemplates) {
      setLoading(false);
    }
  }, [whatsappTemplates]);

  // Match the correct template based on selectedNode's `template.value`
  useEffect(() => {
    if (
      whatsappTemplates?.data?.Data?.List &&
      selectedNode?.data?.template?.value
    ) {
      const matchedTemplate = whatsappTemplates.data.Data.List.find(
        (template) => template.TemplateId === selectedNode.data.template.value
      );

      if (matchedTemplate) {
        setMessageText(matchedTemplate.MessageText);
        setTemplateParams(matchedTemplate.TemplateParameters || []);

        // Initialize placeholders: prioritize saved values, else default to "null"
        let initialPlaceholders = {};
        matchedTemplate.TemplateParameters.forEach((param) => {
          initialPlaceholders[param] =
            selectedNode?.data?.template?.placeholderMappings?.[param] ||
            "null";
        });

        setPlaceholderValues(initialPlaceholders);

        // If there are no parameters, mark it as valid
        setValid(matchedTemplate.TemplateParameters.length === 0);
      } else {
        setMessageText("");
        setTemplateParams([]);
        setValid(false);
      }
    }
  }, [whatsappTemplates, selectedNode]);

  useEffect(() => {
    const existingTriggerMode = selectedNode?.data?.triggerMode;
    if (existingTriggerMode === "api" || existingTriggerMode === "regular") {
      setTriggerMode(existingTriggerMode);
    }
  }, [selectedNode]);

  // Check validity (All placeholders must be filled)
  useEffect(() => {
    if (templateParams.length > 0) {
      const allFilled = templateParams.every(
        (param) => placeholderValues[param] !== "null"
      );
      setValid(allFilled);
    }
  }, [placeholderValues, templateParams]);

  // Handle updates to placeholder selections
  const handlePlaceholderChange = (placeholderKey, value) => {
    setPlaceholderValues((prev) => ({
      ...prev,
      [placeholderKey]: value,
    }));
  };

  // Function to render message text with dropdowns replacing placeholders
  const renderMessageWithSelect = (text) => {
    return text.split(/(\[#\d+#\])/g).map((part, i) => {
      if (/\[#\d+#\]/.test(part)) {
        const placeholderKey = part; // The actual placeholder key in the text
        return (
          <Select
            key={i}
            value={placeholderValues[placeholderKey] || "null"} // Correctly loads saved values
            onChange={(e) =>
              handlePlaceholderChange(placeholderKey, e.target.value)
            }
            size="medium"
            variant="outlined"
            sx={{
              width: "216px",
              minWidth: "216px",
              height: "56px",
              mx: "10px",
              my: "4px",
              color: "#FFFFFF",
              bgcolor: COLOR_HEADER,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: COLOR_ACCENT,
                borderRadius: "0px",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: COLOR_ACCENT,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: COLOR_ACCENT,
              },
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                minHeight: "56px",
                padding: "0 14px",
              },
            }}
            IconComponent={SelectArrow}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: "#1E1E1E",
                  backgroundImage: "none",
                  color: "#FFFFFF",
                  border: `1px solid ${COLOR_ACCENT}`,
                  borderRadius: "0px",
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#1E1E1E",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#FFFFFF",
                    borderRadius: "3px",
                  },
                  "& .MuiMenuItem-root": {
                    minHeight: 42,
                    color: "#FFFFFF",
                    fontSize: "16px",
                    "&.Mui-disabled": {
                      color: "rgba(255,255,255,0.45)",
                    },
                    "&:hover": {
                      backgroundColor: "rgba(13,163,226,0.12)",
                    },
                    "&.Mui-selected, &.Mui-selected:hover": {
                      backgroundColor: "rgba(13,163,226,0.55)",
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="null" disabled>
              Select option
            </MenuItem>
            <MenuItem value="FirstName">First Name</MenuItem>
            <MenuItem value="LastName">Last Name</MenuItem>
            <MenuItem value="FullName">Full Name</MenuItem>
            <MenuItem value="Id">Id</MenuItem>
            <MenuItem value="Phone">Phone</MenuItem>
            <MenuItem value="QrCode">QR Code</MenuItem>
          </Select>
        );
      } else {
        return <span key={i}>{part}</span>; // Render normal text
      }
    });
  };

  // Handle template selection
  // Handle template selection
  const handleSelected = async (key) => {
    try {
      const fullTemplate = await handleGetSingleWhatsappTemplate(
        key.TemplateId
      );
      console.log("Full template response:", fullTemplate);

      setSelected(fullTemplate);
      setTemplateParams(key.TemplateParameters || []);
      setMessageText(key.MessageText);

      // Safely access nested properties with fallbacks
      const templateComponents =
        fullTemplate?.AdditionalData?.TemplateComponents;
      const buttons =
        templateComponents &&
        Array.isArray(templateComponents) &&
        templateComponents.length > 0
          ? templateComponents[0]?.buttons || []
          : [];

      setTemplateButtons(buttons);
      console.log("Template buttons:", buttons);

      // Merge new template with existing saved mappings
      let initialPlaceholders = {};
      (key.TemplateParameters || []).forEach((param) => {
        initialPlaceholders[param] =
          selectedNode?.data?.template?.placeholderMappings?.[param] || "null";
      });

      setPlaceholderValues(initialPlaceholders);

      // If there are no variables, mark it as valid
      setValid((key.TemplateParameters || []).length === 0);
      setTemplateSelectionOpen(false);
    } catch (error) {
      console.error("Error loading WhatsApp template:", error);
      // Handle error gracefully - maybe show a toast notification
      // For now, just set empty values to prevent further errors
      setTemplateButtons([]);
      setTemplateSelectionOpen(false);
    }
  };

  useEffect(() => {
    // Creation flow: template picked before the side-menu opens
    if (preSelectedTemplate && !selectedNode) {
      handleSelected(preSelectedTemplate); // re-use the same logic
    }
  }, [preSelectedTemplate]);

  const handleOpenSelection = () => {
    setTemplateSelectionOpen(true);
  };

  const handleClose = () => {
    setTemplateSelectionOpen(false);
  };

  // Save placeholder mappings when submitting
  const handleSave = () => {
    if (selectedNode) {
      selectedNode.data.triggerMode = triggerMode;
      selectedNode.data.template.placeholderMappings = placeholderValues;
      onSubmit(selected);
    } else {
      const newData = {
        ...selected,
        triggerMode,
        placeholderMappings: placeholderValues,
      };
      console.log(placeholderValues);

      console.log(newData);

      onSubmit(newData);
    }
    onClose();
  };
  return (
    <>
      {/* Prevent loading state from blocking the UI */}
      {isLoading && <Typography sx={{ p: 2 }}>Loading Templates...</Typography>}

      {!isLoading && (
        <>
          <FormTemplate
            title={selectedNode ? "Edit Whatsapp" : "Create Whatsapp"}
            isValid={valid}
            onClose={onClose}
            handleSubmit={handleSave}
            headerRightAction={
              <TriggerModeHeaderControl value={triggerMode} onChange={setTriggerMode} />
            }
            extraLeftAction={
              <Button
                variant="contained"
                color="companyBlue"
                sx={{
                  width: "229px",
                  height: "50px",
                  borderRadius: "7px",
                  textTransform: "none",
                  boxShadow: 0,
                  color: "white",
                  px: "12px",
                  py: "10px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "10px",
                  "&:hover": {
                    backgroundColor: (theme) =>
                      lighten(theme.palette.companyBlue.main, 0.1),
                  },
                }}
                onClick={handleOpenSelection}
              >
                <Box
                  component="span"
                  sx={{
                    width: 18,
                    height: 18,
                    display: "inline-block",
                    backgroundImage: CHANGE_TEMPLATE_ICON_BG,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "contain",
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "Assistant, sans-serif",
                    fontWeight: 700,
                    fontSize: "22px",
                    lineHeight: 1,
                    letterSpacing: 0,
                    color: "#FFFFFF",
                    whiteSpace: "nowrap",
                  }}
                >
                  Change Template
                </Typography>
              </Button>
            }
          >
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >

              {/* Conditional Rendering for Message Text */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                }}
              >
                {!messageText && (
                  <Typography color="#EB4D4D">No Template Selected</Typography>
                )}
              </Box>
              {messageText && (
                <Box>
                  <Box
                    sx={{
                      border: "1px solid rgba(255,255,255,0.55)",
                      borderRadius: "4px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "22px 18px",
                      bgcolor: COLOR_SURFACE,
                      position: "relative",
                      transition: "box-shadow 0.3s ease-in-out",
                      "&:hover": {
                        boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.3)",
                      },
                    }}
                  >
                    {/* Render Message Text with Select Dropdowns */}
                    <Typography
                      variant="body2"
                      sx={{
                        width: "100%",
                        textAlign: "left",
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        columnGap: "6px",
                        rowGap: "10px",
                        color: "#FFFFFF",
                        fontFamily: '"Open Sans Hebrew", sans-serif',
                        fontWeight: 400,
                        fontSize: "22px",
                        lineHeight: 1,
                      }}
                    >
                      {renderMessageWithSelect(messageText)}
                    </Typography>
                  </Box>
                </Box>
              )}
              {templateButtons && (
                <>
                  {templateButtons.map((button) => {
                    return (
                      <Box>
                        <Typography>Button Text: {button.text}</Typography>
                      </Box>
                    );
                  })}
                </>
              )}
            </Box>
          </FormTemplate>
          <WhatsappTemplateSelection
            open={templateSelectionOpen}
            setSelected={handleSelected}
            setClosed={handleClose}
            templates={whatsappTemplates?.data?.Data?.List || []}
            refreshTemplates={handleGetWhatsappTemplates} // Ensure safe access
          />
        </>
      )}
    </>
  );
}
